import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";

const stripMarkdownCodeBlock = (html) => {
  if (!html) return '';
  return html
    .replace(/^```html\s*/i, '') // Remove starting ```html
    .replace(/```$/, '')         // Remove trailing ```
    .trim();
};

function isSafeHtml(html) {
  return typeof html === 'string' && html.trim().length > 0;
}

const UniversalShadowWrapper = ({ htmlContent }) => {
  const hostRef = useRef(null);
  const shadowRootRef = useRef(null);

useEffect(() => {
  if (!hostRef.current) return;

  if (!shadowRootRef.current) {
    shadowRootRef.current = hostRef.current.attachShadow({ mode: 'open' });
  }

  const shadowRoot = shadowRootRef.current;
  shadowRoot.innerHTML = ''; // Clear previous

  const rawHtml = stripMarkdownCodeBlock(htmlContent);
  if (!isSafeHtml(rawHtml)) return;

  try {
    const cleanHtml = DOMPurify.sanitize(rawHtml, {
      ADD_TAGS: ['style', 'script'],
      ADD_ATTR: ['target', 'rel', 'href', 'action', 'type'],
    });

    const container = document.createElement('div');
    container.innerHTML = cleanHtml;

    // Append to Shadow DOM
    Array.from(container.childNodes).forEach((node) => {
      const clone = node.cloneNode(true);

      if (clone.tagName === 'SCRIPT') {
        const newScript = document.createElement('script');
        if (clone.src) newScript.src = clone.src;
        if (clone.type) newScript.type = clone.type;
        newScript.text = clone.textContent;
        shadowRoot.appendChild(newScript);
      } else {
        shadowRoot.appendChild(clone);
      }
    });

    // 🛡️ Prevent all interactions (clicks on links, buttons, etc.)
    const preventDefaultAndStop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.warn(`🚫 Blocked interaction with: <${e.target.tagName.toLowerCase()}>`);
    };

    // Block <a>
    shadowRoot.querySelectorAll('a').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault(); // prevent default only to override behavior
        const url = el.getAttribute('href');
        if (url) {
          window.open(url, '_blank'); // open in new tab
        }
      });
    });


    // Block <button>
    shadowRoot.querySelectorAll('button').forEach((el) => {
      el.addEventListener('click', preventDefaultAndStop);
    });

    // Block <form> submit
    shadowRoot.querySelectorAll('form').forEach((form) => {
      form.addEventListener('submit', preventDefaultAndStop);
    });

  } catch (err) {
    console.error('❌ Error rendering in Shadow DOM:', err);
    shadowRoot.innerHTML = '<h5>Error processing data</h5>';
  }
}, [htmlContent]);



  return <div ref={hostRef} />;
};

export default UniversalShadowWrapper;
